import React, { useState, useRef, useEffect } from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Send, X } from 'lucide-react';
import { useCreateComment, useReplyComment } from '@/hooks/useKBData';
import { useAuth } from '@/providers/AuthProvider';
import { users } from '@/lib/kbApi';

interface CommentComposerProps {
  articleSlug: string;
  parentCommentId?: number;
  onSuccess?: () => void;
  onCancel?: () => void;
  placeholder?: string;
  compact?: boolean;
}

export function CommentComposer({
  articleSlug,
  parentCommentId,
  onSuccess,
  onCancel,
  placeholder = 'Add a comment...',
  compact = false
}: CommentComposerProps) {
  const [body, setBody] = useState('');
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionUsers, setMentionUsers] = useState<any[]>([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { user } = useAuth();
  const createComment = useCreateComment();
  const replyComment = useReplyComment();

  const isReply = !!parentCommentId;
  const mutation = isReply ? replyComment : createComment;

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Fetch users for mentions when query changes
  useEffect(() => {
    if (mentionQuery.length > 0) {
      console.log('🔍 Fetching users for mention query:', mentionQuery);
      users.getAll().then((response) => {
        console.log('✅ Users fetched for mentions:', response);
        const filtered = response.users.filter(u =>
          u.name.toLowerCase().includes(mentionQuery.toLowerCase()) ||
          u.email.toLowerCase().includes(mentionQuery.toLowerCase())
        ).slice(0, 5);
        console.log('🔍 Filtered users:', filtered);
        setMentionUsers(filtered);
        setSelectedMentionIndex(0);
      }).catch((error) => {
        console.error('❌ Failed to fetch users for mentions:', error);
        setMentionUsers([]);
      });
    } else {
      setMentionUsers([]);
    }
  }, [mentionQuery]);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setBody(value);

    // Check for @ mentions
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);

    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setShowMentions(true);
    } else {
      setShowMentions(false);
      setMentionQuery('');
    }
  };

  const insertMention = (mentionUser: any) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = body.substring(0, cursorPos);
    const textAfterCursor = body.substring(cursorPos);

    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    if (atMatch) {
      const beforeAt = textBeforeCursor.substring(0, textBeforeCursor.lastIndexOf('@'));
      const newBody = `${beforeAt}@${mentionUser.name} ${textAfterCursor}`;
      setBody(newBody);

      // Set cursor position after the mention
      const newCursorPos = beforeAt.length + mentionUser.name.length + 2;
      setTimeout(() => {
        textarea.setSelectionRange(newCursorPos, newCursorPos);
        textarea.focus();
      }, 0);
    }

    setShowMentions(false);
    setMentionQuery('');
  };

  const handleSubmit = () => {
    if (!body.trim()) return;

    console.log('💬 Submitting comment:', { body: body.trim(), articleSlug, isReply, parentCommentId });

    if (isReply && parentCommentId) {
      replyComment.mutate({
        commentId: parentCommentId,
        data: { body: body.trim() },
        articleSlug,
      }, {
        onSuccess: (result) => {
          console.log('✅ Reply submitted successfully:', result);
          setBody('');
          onSuccess?.();
        },
        onError: (error) => {
          console.error('❌ Reply submission failed:', error);
        },
      });
    } else {
      createComment.mutate({
        articleSlug,
        data: {
          body: body.trim()
          // Explicitly NOT including parent_id to avoid undefined
        },
      }, {
        onSuccess: (result) => {
          console.log('✅ Comment submitted successfully:', result);
          setBody('');
          onSuccess?.();
        },
        onError: (error) => {
          console.error('❌ Comment submission failed:', error);
        },
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showMentions && mentionUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex((prev) => (prev + 1) % mentionUsers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex((prev) => (prev - 1 + mentionUsers.length) % mentionUsers.length);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        insertMention(mentionUsers[selectedMentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentions(false);
        setMentionQuery('');
        return;
      }
    }

    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape' && isReply) {
      onCancel?.();
    }
  };

  // Auth state checked

  if (!user) {
    return (
      <div className={`${compact ? 'p-3' : 'p-4'} rounded-lg border border-border bg-muted/50 text-center text-sm text-muted-foreground`}>
        Please log in to leave a comment.
      </div>
    );
  }

  return (
    <div className={`${compact ? 'p-3' : 'p-4'} rounded-lg border border-border bg-card`}>
      <div className="flex items-start gap-3">
        <Avatar className="h-8 w-8">
          <AvatarFallback className="text-xs bg-primary/10 text-primary">
            {getUserInitials(user.name || user.email)}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={body}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="w-full min-h-[80px] resize-none text-sm bg-transparent border-none outline-none placeholder:text-muted-foreground"
            disabled={mutation.isPending}
          />

          {/* Mentions dropdown */}
          {showMentions && mentionUsers.length > 0 && (
            <div className="absolute z-50 mt-1 bg-popover border border-border rounded-md shadow-md max-h-48 overflow-auto">
              {mentionUsers.map((mentionUser, index) => (
                <div
                  key={mentionUser.id}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-accent ${
                    index === selectedMentionIndex ? 'bg-accent' : ''
                  }`}
                  onClick={() => insertMention(mentionUser)}
                >
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-xs bg-primary/10 text-primary">
                        {getUserInitials(mentionUser.name || mentionUser.email)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium">{mentionUser.name}</div>
                      <div className="text-muted-foreground text-xs">{mentionUser.email}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-between items-center mt-2">
            <span className="text-xs text-muted-foreground">
              {isReply ? 'Ctrl+Enter to reply • Use @ to mention' : 'Ctrl+Enter to post • Use @ to mention'}
            </span>

            <div className="flex gap-2">
              {isReply && onCancel && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onCancel}
                  disabled={mutation.isPending}
                >
                  <X className="h-3 w-3 mr-1" />
                  Cancel
                </Button>
              )}

              <Button
                size="sm"
                onClick={() => {
                  console.log('🔘 Submit button clicked', { bodyLength: body.trim().length, isPending: mutation.isPending });
                  handleSubmit();
                }}
                disabled={!body.trim() || mutation.isPending}
              >
                <Send className="h-3 w-3 mr-1" />
                {isReply ? 'Reply' : 'Post'}
                {mutation.isPending && ' ...'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}